import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, Heart, Scale } from "lucide-react";
import { toast } from "sonner";
import SelloBadge from "@/components/SelloBadge";
import { useFavorites } from "@/context/FavoritesContext";
import { useCompare } from "@/context/CompareContext";

const ProductCard = ({ product, index = 0 }) => {
  const affiliatePath = `/recomienda/${product.category}/${product.slug}`;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { inCompare, toggleCompare } = useCompare();
  const fav = isFavorite(product.id);
  const cmp = inCompare(product.id);

  const onFav = (e) => {
    e.preventDefault();
    const added = toggleFavorite(product);
    toast[added ? "success" : "message"](added ? "Añadido a favoritos" : "Quitado de favoritos");
  };

  const onCompare = (e) => {
    e.preventDefault();
    const added = toggleCompare(product);
    if (added) toast.success("Añadido al comparador");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.06 }}
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card hover:border-emerald-500/50 hover:shadow-[0_8px_30px_rgba(16,185,129,0.12)] transition-all duration-300 hover:-translate-y-1"
    >
      <Link to={`/producto/${product.id}`} className="block relative aspect-square overflow-hidden bg-secondary">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <SelloBadge />
        </div>
        <button
          data-testid={`fav-btn-${product.id}`}
          onClick={onFav}
          aria-label="Favorito"
          className={`absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full glass transition-all ${fav ? "bg-emerald-500 text-slate-950" : "bg-slate-950/60 text-white hover:bg-slate-950/80"}`}
        >
          <Heart className="h-4 w-4" fill={fav ? "currentColor" : "none"} />
        </button>
        <button
          data-testid={`compare-btn-${product.id}`}
          onClick={onCompare}
          aria-label="Comparar"
          title="Añadir al comparador"
          className={`absolute top-14 right-3 flex h-9 w-9 items-center justify-center rounded-full glass transition-all ${cmp ? "bg-cyan-400 text-slate-950" : "bg-slate-950/60 text-white hover:bg-slate-950/80"}`}
        >
          <Scale className="h-4 w-4" />
        </button>
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-slate-950/70 glass px-2 py-1 font-mono-tech text-[10px] font-bold text-cyan-300">
          {product.purity_score}% pureza
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-emerald-500/80">{product.brand}</span>
        <Link to={`/producto/${product.id}`}>
          <h3 className="mt-1 font-display font-semibold leading-snug line-clamp-2 hover:text-emerald-400 transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {product.tags.slice(0, 3).map((t) => (
            <span key={t} className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {t}
            </span>
          ))}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between">
          {product.price != null && (
            <span className="font-display font-bold text-lg">
              ${product.price.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">{product.currency}</span>
            </span>
          )}
          <a
            data-testid={`buy-btn-${product.id}`}
            href={affiliatePath}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 px-3 py-2 text-xs font-bold text-slate-950 hover:from-emerald-400 hover:to-cyan-400 transition-all"
          >
            Comprar <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
